import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 bg-[#1A1412]" data-testid="news-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
            News & Events
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit']">
            Stay updated with the latest from Vintage Ave Records.
          </p>
        </div>

        {/* Featured Auditions Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 relative overflow-hidden rounded-2xl"
          data-testid="auditions-banner"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_0a80720e-f237-4d6e-94af-756f337617bc/artifacts/xvcl5wfr_IMG_7979.jpeg"
            alt="Auditions & Casting"
            className="w-full h-auto"
          />
        </motion.div>

        {/* Press Release Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
          data-testid="press-release-section"
        >
          <Card className="bg-[#2C221F] border-[#CFB53B]/30">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-semibold">
                  FOR IMMEDIATE RELEASE
                </span>
              </div>
              
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#FDFBF7] mb-6 text-center leading-tight">
                GO! Entrepreneur Magazine™ Announces Strategic Sound Branding Partnership with Vintage Ave Records™
              </h2>
              
              <div className="text-[#A89B95] space-y-4 font-['Outfit'] text-base leading-relaxed">
                <p className="text-[#D97736] font-semibold">
                  ATLANTA, GA — July 1, 2026
                </p>
                
                <p>
                  Vintage Ave Records™, founded by CEO- Phoenix and co-founded by Twig Capone, has entered into a strategic sound branding partnership with GO! Entrepreneur Magazine™, launching with the publication's highly anticipated Great Wealth Transfer Collector's Edition.
                </p>
                
                <p>
                  The special edition features a bold, solution-focused article exploring how men and women can move beyond division to build stronger partnerships, businesses, and generational wealth together.
                </p>
                
                <p>
                  As part of the partnership, Vintage Ave Records™ is prominently featured in the current issue of GO! Entrepreneur Magazine™, giving readers an inside look at the label's creative vision, commitment to artist development, and its growing role in helping businesses establish memorable brand identities through music.
                </p>
                
                <blockquote className="border-l-4 border-[#D97736] pl-6 py-2 my-6 bg-[#1A1412] rounded-r-lg">
                  <p className="text-[#FDFBF7] italic">
                    "Today's brands are no longer recognized only by what people see—they're remembered by what people hear. We originally created a sound trademark with our previous issues using the GO!Entrepreneur™ song created by DiverseCiti™ co-founder Nuke Robinson. This partnership continues to help us represent the future of branding, where music becomes a strategic business asset that builds emotional connections, strengthens brand recognition, and creates lasting consumer experiences."
                  </p>
                  <footer className="text-[#CFB53B] mt-2 text-sm">
                    — CEO of DiverseCiti™, producers of GO!Entrepreneur Magazine™
                  </footer>
                </blockquote>
                
                <p>
                  The collaboration establishes Vintage Ave Records™ as the official sound branding partner for the current issue of GO! Entrepreneur Magazine™, creating opportunities for other original music, signature sonic branding, podcast themes, event soundtracks, and multimedia audio experiences that elevate the publication's growing global platform.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* GO! Entrepreneur Magazine Embed */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
          data-testid="magazine-embed-section"
        >
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-6">
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#FDFBF7] mb-4 text-center">
                Read the Great Wealth Transfer Collector's Edition
              </h3>
              <div className="rounded-lg overflow-hidden">
                <iframe 
                  allowFullScreen={true}
                  allow="autoplay; fullscreen; clipboard-write" 
                  scrolling="no" 
                  className="fp-iframe w-full"
                  src="https://heyzine.com/flip-book/235bce9c24.html" 
                  style={{ border: '1px solid #3D302C', width: '100%', height: '500px' }}
                  title="GO! Entrepreneur Magazine - Great Wealth Transfer Collector's Edition"
                  data-testid="magazine-iframe"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {loading ? (
          <div className="text-center text-[#A89B95] py-8" data-testid="loading-news">
            Loading news...
          </div>
        ) : news.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {news.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`news-card-${index}`}
              >
                <Card className="bg-[#2C221F] border-[#3D302C] hover:border-[#D97736] transition-colors">
                  {item.image_id && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={`${API}/files/${item.image_id}`}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-[#CFB53B] text-sm mb-3">
                      <Calendar size={16} />
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#A89B95] text-sm">{item.content}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Us Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="bg-[#2C221F] border-[#D97736]/30" data-testid="contact-form">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="inline-block bg-[#D97736] text-white px-6 py-2 rounded-full mb-4">
                  <span className="font-['Outfit'] font-semibold">Summer Auditions starts June 5, 2026</span>
                </div>
                <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
                  Get More Information
                </h2>
                <p className="text-[#A89B95] text-lg font-['Outfit'] max-w-2xl mx-auto">
                  Have questions about our artists, upcoming events, or booking inquiries? 
                  We'd love to hear from you. Reach out and we'll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#FDFBF7]">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      placeholder="Your full name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#FDFBF7]">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#FDFBF7]">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    placeholder="(123) 456-7890"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#FDFBF7]">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    placeholder="Tell us about your inquiry..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full py-6 text-lg"
                  data-testid="contact-submit-button"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>

                <p className="text-center text-[#A89B95] text-sm">
                  Or email us directly at{' '}
                  <a href="mailto:vintageaverecords@gmail.com" className="text-[#D97736] hover:text-[#F08F4D]">
                    vintageaverecords@gmail.com
                  </a>
                </p>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}